import React, { useCallback, useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  preventClose?: boolean;
  /**
   * When true, the modal will prompt the user to confirm before dismissing via
   * backdrop click, Escape key, or the header X button. Parents should pass
   * `true` whenever the form has unsaved edits so accidental taps outside the
   * dialog don't wipe the user's work. Explicit cancel / save buttons in the
   * footer should still close the modal without a confirm (they call onClose
   * themselves, bypassing this guard). (Bug #141)
   */
  isDirty?: boolean;
  /**
   * Text shown in the dirty-state confirm dialog. Defaults to a generic copy.
   */
  dirtyConfirmMessage?: string;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  preventClose = false,
  isDirty = false,
  dirtyConfirmMessage = 'You have unsaved changes. Discard them and close this dialog?',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Keep a stable ref for onClose so the focus-trap effect doesn't re-run
  // (and steal focus) every time the parent re-renders with a new callback.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Track the latest dirty flag + confirm copy in refs too so the keydown
  // listener doesn't need to be torn down on every parent re-render.
  const isDirtyRef = useRef(isDirty);
  const dirtyMsgRef = useRef(dirtyConfirmMessage);
  useEffect(() => {
    isDirtyRef.current = isDirty;
    dirtyMsgRef.current = dirtyConfirmMessage;
  }, [isDirty, dirtyConfirmMessage]);

  // Centralised "maybe close" helper — returns true if the modal actually
  // closed. If the form is dirty, ask the user before discarding their work
  // (Bug #141: previously, any backdrop click or Escape silently destroyed
  // in-progress inputs).
  const requestClose = useCallback((): boolean => {
    if (preventClose) return false;
    if (isDirtyRef.current) {
      // window.confirm is intentionally simple here — the design system does
      // not ship its own confirm dialog and adding one is out-of-scope for
      // this bug fix. It blocks the event loop so focus / keyboard behaviour
      // is unaffected.
      const ok = typeof window !== 'undefined'
        ? window.confirm(dirtyMsgRef.current)
        : true;
      if (!ok) return false;
    }
    onCloseRef.current();
    return true;
  }, [preventClose]);

  const sizeMap = {
    sm: 400,
    md: 600,
    lg: 800,
  };

  useEffect(() => {
    if (!isOpen) return;

    // Focus trap — find the first *input-like* element to focus rather than
    // the close button, so that keyboard users land inside the form.
    const previousActiveElement = document.activeElement as HTMLElement;

    const getFocusableElements = () =>
      contentRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      ) as NodeListOf<HTMLElement> | undefined;

    const focusableElements = getFocusableElements();

    // Prefer focusing the first input/select/textarea; fall back to first focusable.
    const firstInput = contentRef.current?.querySelector(
      'input, select, textarea'
    ) as HTMLElement | null;
    const firstElement = focusableElements?.[0];
    const lastElement = focusableElements?.[focusableElements.length - 1];

    if (firstInput) {
      firstInput.focus();
    } else if (firstElement) {
      firstElement.focus();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !preventClose) {
        // Route through requestClose so Escape also respects the dirty guard.
        requestClose();
      }

      if (e.key === 'Tab') {
        // Re-query in case DOM changed (dynamic content)
        const els = getFocusableElements();
        if (els && els.length > 0) {
          const first = els[0];
          const last = els[els.length - 1];
          if (e.shiftKey) {
            if (document.activeElement === first) {
              e.preventDefault();
              last?.focus();
            }
          } else {
            if (document.activeElement === last) {
              e.preventDefault();
              first?.focus();
            }
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previousActiveElement && previousActiveElement !== document.body) {
        previousActiveElement.focus();
      }
    };
  }, [isOpen, preventClose, requestClose]);

  if (!isOpen) return null;

  const backdropStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease-in-out',
  };

  const contentStyle: React.CSSProperties = {
    backgroundColor: 'var(--color-white)',
    borderRadius: '8px',
    width: '90vw',
    maxWidth: sizeMap[size],
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
    animation: 'fadeIn 0.2s ease-in-out',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid var(--color-light-grey)',
    flexShrink: 0,
  };

  const titleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    fontFamily: 'var(--font-family)',
    color: 'var(--color-black)',
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-dark-grey)',
    transition: 'color 0.2s ease-in-out',
  };

  const bodyStyle: React.CSSProperties = {
    flex: 1,
    overflow: 'auto',
    padding: '20px',
    fontFamily: 'var(--font-family)',
    color: 'var(--color-dark-grey)',
  };

  const footerStyle: React.CSSProperties = {
    padding: '16px 20px',
    borderTop: '1px solid var(--color-light-grey)',
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    flexShrink: 0,
    backgroundColor: 'var(--color-light-grey)',
  };

  const handleBackdropClick = () => {
    // Route through the dirty-state guard so accidental clicks on the
    // backdrop don't wipe in-progress form data (Bug #141).
    requestClose();
  };

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
      <div style={backdropStyle} onClick={handleBackdropClick} ref={modalRef}>
        <div
          style={contentStyle}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          onClick={(e) => e.stopPropagation()}
          ref={contentRef}
        >
          <header style={headerStyle}>
            <h2 id="modal-title" style={titleStyle}>
              {title}
            </h2>
            {!preventClose && (
              <button
                style={closeButtonStyle}
                onClick={() => requestClose()}
                aria-label="Close modal"
                onMouseEnter={(e) => {
                  (e.target as HTMLButtonElement).style.color = 'var(--color-black)';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.color = 'var(--color-dark-grey)';
                }}
              >
                <X size={20} />
              </button>
            )}
          </header>
          <div style={bodyStyle}>{children}</div>
          {footer && <footer style={footerStyle}>{footer}</footer>}
        </div>
      </div>
    </>
  );
};
